import { useMemo, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Palette, ChevronDown, ChevronRight, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';

type Scheme = 'complementary' | 'analogous' | 'triadic' | 'splitComplementary';

const SCHEMES: Array<{ key: Scheme; label: string }> = [
  { key: 'complementary', label: 'Complementary' },
  { key: 'analogous', label: 'Analogous' },
  { key: 'triadic', label: 'Triadic' },
  { key: 'splitComplementary', label: 'Split' },
];

/**
 * Per-room colour palette helper. Pick a hue from the ring, type hex / RGB,
 * see the complementary (or other harmony) for pairing reference. Default seed
 * is a warm brown so the opposite-of-brown demo Karl asked for surfaces on
 * first open.
 *
 * No persistence — pure tool. If we end up wanting a saved accent colour per
 * room, add `accent_color text` to renovation_rooms and a Save button.
 */
export function RoomColorWheel({ seedHex = '#8B5A2B' }: { seedHex?: string }) {
  const [expanded, setExpanded] = useState(false);
  const [hex, setHex] = useState(seedHex);
  const [scheme, setScheme] = useState<Scheme>('complementary');
  const ringRef = useRef<HTMLDivElement | null>(null);

  const rgb = hexToRgb(hex) ?? { r: 0, g: 0, b: 0 };
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const harmonyHexes = useMemo(() => harmonyFromHex(hex, scheme), [hex, scheme]);

  const onWheelPointer = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = ringRef.current;
    if (!el) return;
    // Only follow drag once the user has actually committed to interacting.
    if (e.type === 'pointermove' && e.buttons !== 1) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    // atan2 in CSS conic-gradient terms: 0deg is straight up. We rotate so
    // red sits at the top of the wheel; this matches the conic-gradient
    // background which starts with red.
    const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
    const hue = (angleDeg + 360) % 360;
    const next = hslToHex(hue, Math.max(40, hsl.s), 50);
    setHex(next);
  };

  const onHexInput = (raw: string) => {
    const cleaned = raw.startsWith('#') ? raw : `#${raw}`;
    setHex(cleaned);
    // Re-emit normalised if it parses, so the input stabilises.
    const parsed = hexToRgb(cleaned);
    if (parsed) {
      setHex(rgbToHex(parsed.r, parsed.g, parsed.b));
    }
  };

  const onRgbInput = (channel: 'r' | 'g' | 'b', value: string) => {
    const n = Math.max(0, Math.min(255, Math.round(Number(value) || 0)));
    const next = { ...rgb, [channel]: n };
    setHex(rgbToHex(next.r, next.g, next.b));
  };

  const copyHex = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${value} copied`);
    } catch {
      toast.error('Clipboard blocked');
    }
  };

  // Position of the indicator pip on the ring. Center of the ring + r·sin/cos
  // of the hue angle. Matches the conic gradient (0deg = top, clockwise).
  const indicatorStyle = useMemo(() => {
    const radius = 50; // percentage from centre
    const angleRad = ((hsl.h - 90) * Math.PI) / 180;
    const x = 50 + radius * Math.cos(angleRad);
    const y = 50 + radius * Math.sin(angleRad);
    return { left: `${x}%`, top: `${y}%` };
  }, [hsl.h]);

  if (!expanded) {
    return (
      <Card>
        <CardContent className="flex items-center justify-between p-3">
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronRight className="h-4 w-4" />
            <Palette className="h-4 w-4" />
            Colour palette
          </button>
          <div className="flex items-center gap-1.5">
            <Swatch hex={hex} title="current" size="sm" />
            <span className="text-[10px] text-muted-foreground">↔</span>
            <Swatch hex={harmonyHexes[1] ?? hex} title="opposite" size="sm" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="flex items-center gap-2 text-sm font-medium hover:text-foreground/80"
        >
          <ChevronDown className="h-4 w-4" />
          <Palette className="h-4 w-4" />
          Colour palette
        </button>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-[auto_1fr]">
          {/* Wheel + inputs column */}
          <div className="flex flex-col items-center gap-3">
            <div
              ref={ringRef}
              role="slider"
              aria-label="Hue selector"
              aria-valuenow={Math.round(hsl.h)}
              tabIndex={0}
              onPointerDown={onWheelPointer}
              onPointerMove={onWheelPointer}
              className="relative h-44 w-44 cursor-crosshair touch-none select-none rounded-full"
              style={{
                background:
                  'conic-gradient(from 0deg, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
              }}
            >
              {/* Inner hole so the wheel reads as a ring */}
              <div className="absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full bg-background" />
              {/* Current colour preview in the centre */}
              <div
                className="absolute left-1/2 top-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full border border-border shadow-inner"
                style={{ background: hex }}
                aria-hidden
              />
              {/* Pip on the ring at the current hue */}
              <div
                className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background bg-foreground shadow"
                style={indicatorStyle}
                aria-hidden
              />
            </div>

            <div className="flex w-full flex-col gap-1.5 text-xs">
              <label className="flex items-center gap-2">
                <span className="w-6 shrink-0 text-muted-foreground">hex</span>
                <Input
                  value={hex}
                  onChange={(e) => onHexInput(e.target.value)}
                  className="h-8 font-mono text-xs uppercase"
                  spellCheck={false}
                />
              </label>
              <div className="flex items-center gap-1.5">
                <span className="w-6 shrink-0 text-muted-foreground">rgb</span>
                <Input
                  value={rgb.r}
                  type="number"
                  min={0}
                  max={255}
                  onChange={(e) => onRgbInput('r', e.target.value)}
                  className="h-8 font-mono text-xs"
                />
                <Input
                  value={rgb.g}
                  type="number"
                  min={0}
                  max={255}
                  onChange={(e) => onRgbInput('g', e.target.value)}
                  className="h-8 font-mono text-xs"
                />
                <Input
                  value={rgb.b}
                  type="number"
                  min={0}
                  max={255}
                  onChange={(e) => onRgbInput('b', e.target.value)}
                  className="h-8 font-mono text-xs"
                />
              </div>
            </div>
          </div>

          {/* Harmony column */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-1">
              {SCHEMES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setScheme(s.key)}
                  className={cn(
                    'rounded-md border px-2 py-1 text-[11px] transition-colors',
                    scheme === s.key
                      ? 'border-foreground text-foreground'
                      : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              {harmonyHexes.map((h, i) => (
                <div key={i} className="flex flex-col items-start gap-1">
                  <Swatch hex={h} title={i === 0 ? 'base' : `pair ${i}`} size="lg" />
                  <button
                    type="button"
                    onClick={() => copyHex(h)}
                    className="inline-flex items-center gap-1 font-mono text-[10px] uppercase text-muted-foreground hover:text-foreground"
                    title="Copy hex"
                  >
                    {h}
                    <Copy className="h-2.5 w-2.5" />
                  </button>
                  <span className="text-[10px] text-muted-foreground">
                    rgb({hexToRgb(h)?.r ?? 0}, {hexToRgb(h)?.g ?? 0}, {hexToRgb(h)?.b ?? 0})
                  </span>
                </div>
              ))}
            </div>

            <div className="rounded-md border border-border bg-background/40 p-2">
              <p className="mb-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                Preview
              </p>
              <div
                className="flex h-14 items-center justify-center rounded text-sm font-medium"
                style={{
                  background: hex,
                  color: harmonyHexes[1] ?? hex,
                  border: `1px solid ${harmonyHexes[1] ?? hex}`,
                }}
              >
                base on accent
              </div>
              <div
                className="mt-1.5 flex h-14 items-center justify-center rounded text-sm font-medium"
                style={{
                  background: harmonyHexes[1] ?? hex,
                  color: hex,
                  border: `1px solid ${hex}`,
                }}
              >
                accent on base
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Swatch({
  hex,
  title,
  size = 'md',
}: {
  hex: string;
  title?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const dim =
    size === 'lg' ? 'h-14 w-14' : size === 'md' ? 'h-9 w-9' : 'h-5 w-5';
  return (
    <div
      className={cn(dim, 'rounded-md border border-border shadow-inner')}
      style={{ background: hex }}
      title={`${title ?? ''} ${hex}`.trim()}
    />
  );
}

// --- Colour maths ---

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = hex.replace('#', '').match(/^([0-9a-f]{6})$/i);
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return (
    '#' +
    [clamp(r), clamp(g), clamp(b)]
      .map((v) => v.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()
  );
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn:
        h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
        break;
      case gn:
        h = ((bn - rn) / d + 2) * 60;
        break;
      case bn:
        h = ((rn - gn) / d + 4) * 60;
        break;
    }
  }
  return { h, s: s * 100, l: l * 100 };
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  const sn = s / 100;
  const ln = l / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const hp = ((h % 360) + 360) % 360 / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hp >= 0 && hp < 1) [r1, g1, b1] = [c, x, 0];
  else if (hp < 2) [r1, g1, b1] = [x, c, 0];
  else if (hp < 3) [r1, g1, b1] = [0, c, x];
  else if (hp < 4) [r1, g1, b1] = [0, x, c];
  else if (hp < 5) [r1, g1, b1] = [x, 0, c];
  else if (hp < 6) [r1, g1, b1] = [c, 0, x];
  const m = ln - c / 2;
  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  };
}

function hslToHex(h: number, s: number, l: number): string {
  const { r, g, b } = hslToRgb(h, s, l);
  return rgbToHex(r, g, b);
}

/**
 * Return a small palette starting with the base colour, then one or more
 * harmony partners. The scheme drives which hue offsets we use.
 */
function harmonyFromHex(hex: string, scheme: Scheme): string[] {
  const rgb = hexToRgb(hex);
  if (!rgb) return [hex];
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const rot = (h: number, delta: number) => hslToHex((h + delta + 360) % 360, hsl.s, hsl.l);
  switch (scheme) {
    case 'complementary':
      return [hex, rot(hsl.h, 180)];
    case 'analogous':
      return [hex, rot(hsl.h, -30), rot(hsl.h, 30)];
    case 'triadic':
      return [hex, rot(hsl.h, 120), rot(hsl.h, 240)];
    case 'splitComplementary':
      return [hex, rot(hsl.h, 150), rot(hsl.h, 210)];
    default:
      return [hex];
  }
}
